import { CaretRightIcon } from '@phosphor-icons/react'
import { useEffect, useMemo } from 'react'
import { useQuery } from 'urql'
import { Badge, type BadgeColor } from 'app/components/ui/badge'
import { graphql } from 'app/generated/gql'
import type { ActionCenterExitStageCountQuery } from 'app/generated/gql/graphql'
import { MODEL_FIELDS } from 'app/generated/modelFields'
import { ContactJson } from 'app/types/entities'

/** How many a list will load before it stops asking for more. */
export const EXIT_PAGE = 50

/**
 * Everybody a board has sent to one of its exits.
 *
 * A card dropped on Scheduled or Lost leaves the board, and until now that was
 * the end of it: the queue's own search no longer matches them (a patient who
 * books is, definitionally, not a patient waiting to book), so nothing on the
 * page could answer "who did we book this week". The pipeline membership
 * outlives the queue, and that is what this reads.
 *
 * `pipelines.key` and not the pair of clauses it looks like it could be.
 * `pipelines.id:"system:hygiene" pipelines.stage:"Scheduled"` reads correctly
 * and is wrong: the memberships are indexed flat, so the two clauses are not
 * tied to the same one, and a patient who is Scheduled in Unscheduled
 * Treatment would count as Scheduled in Hygiene Recall. Measured on live data
 * before this was written: `pipelines.stage` is analysed, so "Started" matched
 * all 45 contacts sitting at "Not started"; and one contact is in both system
 * pipelines, which the paired form counted under the wrong pipeline's stage.
 * `pipelines.key` is the composite `<pipelineId>::<stageName>` the backend
 * indexes for exactly this, and what the contacts table's own pipeline-stage
 * filter searches on.
 */
export function exitStageSearch(pipelineId: string, stage: string): string {
  return `pipelines.key:"${pipelineId}::${stage}"`
}

const EXIT_STAGE_COUNT_QUERY = graphql(`
  query ActionCenterExitStageCount($search: String, $pagination: PaginationInput) {
    contacts(search: $search, pagination: $pagination) {
      total
    }
  }
`)

/**
 * @param nonce Bumped by the board on every write that lands, to re-read.
 *   urql's document cache keys on the query and its variables, and neither
 *   moves when somebody drops a case on an exit, so without this the count is
 *   frozen at whatever it was when the page loaded: you drop three cases on
 *   Scheduled and the zone goes on saying nobody is there, which is the one
 *   moment you are looking straight at it.
 */
export function useContactSearchCount(search: string | undefined, nonce = 0): number | undefined {
  const [result, reexecute] = useQuery<ActionCenterExitStageCountQuery>({
    query: EXIT_STAGE_COUNT_QUERY,
    variables: { search: search ?? ``, pagination: { limit: 0 } },
    pause: search === undefined,
  })
  useEffect(() => {
    // Not on the first render: the query above has just run.
    if (!nonce || search === undefined) return
    // `network-only`, since the cached answer is precisely what is wrong.
    reexecute({ requestPolicy: `network-only` })
  }, [nonce, search, reexecute])
  // Quiet on error: a drop target without its count is still a drop target.
  return result.data?.contacts.total
}

export function useExitStageCount(pipelineId: string | undefined, stage: string, nonce = 0): number | undefined {
  return useContactSearchCount(pipelineId ? exitStageSearch(pipelineId, stage) : undefined, nonce)
}

/**
 * The contacts behind one of those counts, on request.
 *
 * Fetched when the list opens rather than with the board: it answers a
 * question somebody asks occasionally, and a board already spends its requests
 * on the lanes people work every day.
 */
const EXIT_STAGE_CONTACTS_QUERY = graphql(`
  query ActionCenterExitStageContacts($search: String, $pagination: PaginationInput, $sort: SortInput) {
    contacts(search: $search, pagination: $pagination, sort: $sort) {
      hits {
        version
        createdDate
        updatedDate
        id
        patientIds
        patientName
        orgId
        workspaceId
        locationId
        name
        preferredName
        homePhone
        cellPhone
        workPhone
        otherPhone
        email
        birthDate
        addressLine1
        addressLine2
        lifecycleStage
        patientStatus
        leadStatus
        isQualified
        appointmentStatus
        nearestAppointmentDate
        city
        state
        zip
        becameLeadDate
        becamePatientDate
        patientCreatedDate
        firstAppointmentMadeDate
        firstAppointmentDate
        lastAppointmentDate
        firstTransactionDate
        lastTransactionDate
        lastHygieneDate
        hygieneDueDate
        isPerio
        tags
        matchTypes
        collectedLtv
        productionLtv
        scheduledProduction
        unbookedTreatmentValue
        lastTreatmentPlanDate
        fvv
        adClickDate
        patientReferral
        lastActivityDate
        lastCommDate
        lastCommActivityType
        lastCommActivitySubType
        utmSource
        utmMedium
        utmCampaign
        utmKeywords
        utmContent
        platform
        trackableCampaignId
        leadOwner {
          id
          name
          aliases
          userId
        }
        channel
        smsMarketingConsent
        firstContactReason
        firstActivityType
        activityTypes
        convertedBy {
          id
          name
          aliases
          userId
        }
        firstActivityDate
        becameInactiveDate
        closedLostReason
        preferredCommunicationMethod
        preferredContact
        archived
        pipelines {
          id
          stage
          key
        }
        neededAction
        reportedAttribution
        activityCount
        needsActionDate
        firstName
        lastName
      }
      total
    }
  }
`)

export function useExitStageContacts(
  pipelineId: string | undefined,
  stage: string,
  open: boolean,
): ContactJson[] | undefined {
  const variables = useMemo(
    () => ({
      search: exitStageSearch(pipelineId ?? ``, stage),
      pagination: { limit: EXIT_PAGE },
      sort: { field: `updatedDate`, direction: `DESC` },
    }),
    [pipelineId, stage],
  )
  const [result] = useQuery<{ contacts?: { hits?: ContactJson[] } }>({
    query: EXIT_STAGE_CONTACTS_QUERY,
    variables,
    pause: !open || !pipelineId,
    // The list opens onto cards somebody may just have dragged there, so a
    // cached answer shows immediately and the network refreshes it.
    requestPolicy: `cache-and-network`,
  })
  if (result.error) return []
  return result.data?.contacts?.hits
}

/**
 * The count on an exit, and the promise that pressing the zone opens what is
 * behind it.
 *
 * Not a button of its own any more: the whole zone opens the list, and a
 * separate hit target inside it meant the one number people aimed at was a
 * few pixels wide.
 *
 * The caret shows on hover whether or not there is a count, and the zone is
 * openable at zero for the same reason: an exit somebody has just dropped a
 * case on can read zero for a moment, and an exit nobody has used this session
 * still holds everyone the practice sent there before today. Gating the way in
 * on the number meant the emptier the zone looked, the harder it was to check.
 */
export function ExitStageCount({
  count,
  label,
  color,
}: {
  count: number | undefined
  /** The stage's own word, so the number says what it is counting. */
  label: string
  color: BadgeColor
}) {
  return (
    <span className="inline-flex items-center gap-1">
      {/* The house badge rather than a bare number: `chipBase` fixes the
          height and `align-middle`, which is what stops the figure riding
          above the line of the sentence it sits next to. */}
      {!!count && <Badge color={color} label={`${count} ${label.toLowerCase()}`} />}
      <CaretRightIcon size={12} weight="bold" className="opacity-0 transition-opacity group-hover:opacity-100" />
    </span>
  )
}

/** Options every exit-stage list passes to `showModal`, minus the title. */
export const EXIT_STAGE_MODAL_OPTIONS = {
  useRadix: true,
  isFullScreen: false,
  removePadding: true,
  overflowHidden: true,
  // A definite width, not a cap. `min-w-0` here beats the modal's own
  // `min-w-full` in twMerge, so the panel shrink-wraps whatever is inside it
  // and a `max-w` alone leaves the cards to collapse to their own text. 30rem
  // puts a card at ~456px, comfortably past the ~323px it gets in a board
  // lane, which is the width this list is read against.
  panelClassName: `min-w-0 w-full sm:w-[30rem] sm:max-w-[94vw]`,
} as const

/**
 * The body of an exit-stage list, shown through the app's modal system. The
 * cards are the same ones the lanes draw; `children` because the two boards
 * draw different cards from different data. The grey ground is the board's
 * own, so a card reads here the way it reads in a lane.
 */
export function ExitStageListBody({
  count,
  loading,
  empty,
  children,
}: {
  count: number
  loading?: boolean
  empty?: boolean
  children?: React.ReactNode
}) {
  // One card a row, and the list scrolls rather than the modal growing: a
  // stage can hold fifty of these, and the panel's own `max-h-[90vh]` would
  // otherwise be the only thing stopping it.
  return (
    <div className="max-h-[75vh] overflow-y-auto bg-surface-muted p-3 text-left">
      {loading && <p className="px-1 py-2 text-body-sm text-subtle">Reading.</p>}
      {!loading && empty && <p className="px-1 py-2 text-body-sm text-subtle">Nobody yet.</p>}
      {/* The cards fill the panel; the panel is what carries the width (see
          EXIT_STAGE_MODAL_OPTIONS), so a case is drawn at a lane's proportions
          rather than stretched across the screen. */}
      <div className="flex flex-col gap-2">{children}</div>
      {count > EXIT_PAGE && (
        // Said rather than silently truncated.
        <p className="px-1 pt-3 text-body-xs text-subtle">
          Showing the {EXIT_PAGE} most recently updated of {count}.
        </p>
      )}
    </div>
  )
}
